import { useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";

// Bandeau publication vitrine — visible sur RestaurateurDashboard.
// 3 états :
//   1. Publié → bandeau vert + bouton "Retirer temporairement"
//   2. Prêt à publier (minima OK) → bandeau orange + bouton "Publier maintenant"
//   3. Incomplet → bandeau gris + liste des minima manquants
export default function PublishBanner({ gate, onChanged }) {
  const [busy, setBusy] = useState(false);
  if (!gate) return null;

  const publish = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/restaurateur/publish");
      toast.success("🎉 Votre vitrine est en ligne sur Nold Go !");
      // Optimistic update — on notifie le parent avec l'état frais pour éviter
      // qu'un refetch lent laisse l'UI en état "brouillon" quelques secondes.
      onChanged?.({ publishedAt: data?.published_at, restaurant: data?.restaurant });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publication impossible.");
    } finally { setBusy(false); }
  };

  const unpublish = async () => {
    if (!window.confirm("Retirer votre vitrine de Nold Go ? Les clients ne pourront plus commander tant qu'elle est retirée.")) return;
    setBusy(true);
    try {
      await api.post("/restaurateur/unpublish");
      toast.success("Vitrine retirée temporairement.");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur.");
    } finally { setBusy(false); }
  };

  // VERROU ADMIN — bandeau rouge non contournable, prioritaire sur tout autre état.
  if (gate.admin_locked) {
    return (
      <div data-testid="publish-banner-admin-locked" className="mt-5 rounded-3xl p-5 bg-red-50 border-2 border-red-500">
        <div className="flex items-start gap-3">
          <div className="text-3xl">🚫</div>
          <div className="flex-1">
            <div className="font-head font-black text-lg text-red-800">Vitrine désactivée par l'administration</div>
            <div className="text-sm text-red-700 mt-1">
              {gate.admin_lock_reason
                ? <>Motif indiqué par l'administration : <b>« {gate.admin_lock_reason} »</b>.</>
                : "Votre vitrine a été mise hors ligne par l'équipe Nold Go."}
            </div>
            <div className="text-sm text-red-700 mt-2">
              Seul un administrateur peut réactiver votre vitrine. Contactez le support :
            </div>
            <a href="mailto:nold.contact@noldgo.com?subject=Réactivation%20de%20ma%20vitrine%20Nold%20Go"
               data-testid="publish-banner-contact-support"
               className="mt-3 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-sm">
              ✉️ Contacter le support
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Publié
  if (gate.published) {
    return (
      <div data-testid="publish-banner-live" className="mt-5 rounded-3xl p-5 bg-[#16a34a] text-white shadow-[0_8px_30px_rgba(22,163,74,0.25)]">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="text-3xl">🟢</div>
            <div>
              <div className="font-head font-black text-lg">Votre vitrine est en ligne sur Nold Go</div>
              <div className="text-sm text-white/90 mt-0.5">
                Les clients peuvent découvrir votre menu et commander en ce moment même.
              </div>
            </div>
          </div>
          <button data-testid="publish-unpublish-btn" onClick={unpublish} disabled={busy}
            className="h-10 px-4 rounded-lg bg-white/15 hover:bg-white/25 text-white text-sm font-bold whitespace-nowrap disabled:opacity-40">
            {busy ? "…" : "📴 Retirer temporairement"}
          </button>
        </div>
      </div>
    );
  }

  // Prêt à publier
  if (gate.can_publish) {
    return (
      <div data-testid="publish-banner-ready" className="mt-5 rounded-3xl p-5 bg-gradient-to-br from-[#F26419] to-[#D94F2B] text-white shadow-[0_8px_30px_rgba(242,100,25,0.3)]">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="text-3xl">🚀</div>
            <div>
              <div className="font-head font-black text-lg">Prêt à passer en ligne ?</div>
              <div className="text-sm text-white/90 mt-0.5">
                Votre vitrine est complète ({gate.n_items_active} plats actifs). Publiez-la sur Nold Go pour commencer à recevoir des commandes.
              </div>
            </div>
          </div>
          <Button data-testid="publish-btn" onClick={publish} disabled={busy}
            className="h-11 px-5 rounded-xl bg-white text-[#F26419] font-black text-sm whitespace-nowrap hover:bg-white/95 disabled:opacity-40">
            {busy ? "Publication…" : "Publier maintenant →"}
          </Button>
        </div>
        {/* Alerte photo manquante — logo présent mais pas de bandeau : la carte sera vide sur la galerie */}
        {gate.warn_no_cover_photo && (
          <div data-testid="publish-banner-no-cover-warning"
            className="mt-3 rounded-2xl bg-white/15 border border-white/25 p-3 text-sm text-white flex items-start gap-2">
            <span className="text-lg leading-none">📸</span>
            <div>
              <b>Aucune photo bandeau ajoutée</b> — votre carte apparaîtra <b>sans image</b> sur la galerie de noldgo.com,
              ce qui divise par 3 le taux de clic. Ajoutez une belle photo depuis l'onglet <b>Profil</b> pour attirer plus de clients.
            </div>
          </div>
        )}
      </div>
    );
  }

  // Incomplet
  return (
    <div data-testid="publish-banner-draft" className="mt-5 rounded-3xl p-5 bg-white border-2 border-[#F59E0B]">
      <div className="flex items-start gap-3">
        <div className="text-2xl">✏️</div>
        <div className="flex-1">
          <div className="font-head font-black text-base text-[#92400E]">Votre vitrine est en brouillon</div>
          <div className="text-sm text-[#78350F] mt-1">
            Pour publier sur Nold Go, complétez ces éléments :
          </div>
          <ul className="mt-2 space-y-1">
            {gate.missing.map((m, i) => (
              <li key={i} data-testid={`publish-missing-${i}`} className="text-sm text-[#B45309] flex items-center gap-2">
                <span>⚠️</span> {m}
              </li>
            ))}
          </ul>
          <div className="mt-3 text-xs text-[#75807A]">
            💡 Ajoutez vos plats depuis l'onglet <b>Menu</b> et la photo depuis l'onglet <b>Profil</b>.
          </div>
        </div>
      </div>
    </div>
  );
}
